import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { ITemplateFormValues } from 'features/Vehicles/types'
import { Select } from 'components/v2/Select'
import { InputNumber } from 'components/InputNumber'
import { SelectVehicleTemplate } from 'features/VehicleTemplates/components/SelectVehicleTemplate'
import { vehicleTypeMessages } from 'features/Vehicles/messages'
import { safeFormatMessage } from '@10xlabs/utils'
import { TVehicleTemplateType } from 'gql/generated/graphql'

const VEHICLE_TYPE_LIST = [
  TVehicleTemplateType.Bus,
  TVehicleTemplateType.Van,
  TVehicleTemplateType.Sedan,
  TVehicleTemplateType.Suv
]

const messages = defineMessages({
  typePlaceholder: {
    id: 'vehicles.form.template.type.placeholder',
    defaultMessage: 'Select type'
  },
  capacityPlaceholder: {
    id: 'vehicles.form.template.capacity.placeholder',
    defaultMessage: 'Number of seats'
  }
})

export const TemplateForm = ({ isEdit }: { isEdit?: boolean }): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setValues, handleBlur, touched, errors } = useFormikContext<ITemplateFormValues>()

  const handleTypeChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
    setValues({
      ...values,
      template: {
        ...values.template,
        type: value as TVehicleTemplateType,
        id: ''
      }
    })
  }

  const handleCapacityChange = (value: string): void => {
    setValues({
      ...values,
      template: {
        ...values.template,
        capacity: value ? parseInt(value) : '',
        id: ''
      }
    })
  }

  return (
    <>
      <Heading.H4>
        <FormattedMessage id="vehicles.form.template.title" defaultMessage="Template" />
      </Heading.H4>
      <Row noMargin>
        <Column>
          <Select
            id="template.type"
            value={values.template.type}
            onChange={handleTypeChange}
            onBlur={handleBlur}
            touched={touched.template?.type}
            errors={errors.template?.type}
            label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
            disabled={isEdit}
          >
            <option value="">{intl.formatMessage(messages.typePlaceholder)}</option>
            {VEHICLE_TYPE_LIST.map((type): TSFixMe => {
              return (
                <option key={type} value={type}>
                  {safeFormatMessage({ intl, messages: vehicleTypeMessages, key: type })}
                </option>
              )
            })}
          </Select>
        </Column>
        <Column>
          <InputNumber
            id="template.capacity"
            onChange={handleCapacityChange}
            onBlur={handleBlur}
            value={values.template.capacity}
            placeholder={intl.formatMessage(messages.capacityPlaceholder)}
            touched={touched.template?.capacity}
            errors={errors.template?.capacity}
            maskOptions={{
              allowDecimal: false
            }}
            label={<FormattedMessage id="dictionary.capacity" defaultMessage="Capacity" />}
            disabled={isEdit}
          />
        </Column>
        <Column>
          <SelectVehicleTemplate
            id="template.id"
            value={values.template.id}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.template?.id}
            errors={errors.template?.id}
            label={<FormattedMessage id="dictionary.template" defaultMessage="Template" />}
            capacity={values.template.capacity}
            type={values.template.type}
            disabled={isEdit}
          />
        </Column>
      </Row>
    </>
  )
}
